import React from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const TypingSimulationControls = ({ 
  enableTyping, 
  typingSpeed, 
  showTypingIndicator, 
  onEnableTypingChange, 
  onTypingSpeedChange, 
  onShowTypingIndicatorChange 
}) => {
  const speedOptions = [
    { value: 'slow', label: 'Langsam (2-3 Sekunden)', description: 'Natürliches, bedachtes Tippen' },
    { value: 'medium', label: 'Mittel (1-2 Sekunden)', description: 'Ausgewogene Geschwindigkeit' },
    { value: 'fast', label: 'Schnell (0.5-1 Sekunde)', description: 'Schnelle Antworten' },
    { value: 'instant', label: 'Sofort (0 Sekunden)', description: 'Keine Verzögerung' }
  ];

  const getSpeedDescription = (speed) => {
    const descriptions = {
      slow: 'Simuliert natürliches, bedachtes Tippen mit längeren Pausen',
      medium: 'Ausgewogene Geschwindigkeit für realistische Interaktion',
      fast: 'Schnelle Antworten für effiziente Kommunikation',
      instant: 'Sofortige Antworten ohne Typing-Animation'
    };
    return descriptions?.[speed] || descriptions?.medium;
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-foreground">Typing-Simulation</h3>
      {/* Enable Typing Simulation */}
      <div className="space-y-3">
        <Checkbox
          label="Typing-Simulation aktivieren"
          description="Simuliert natürliches Tippen für realistischere Interaktionen"
          checked={enableTyping}
          onChange={(e) => onEnableTypingChange(e?.target?.checked)}
        />
      </div>
      {enableTyping && (
        <>
          {/* Typing Speed */}
          <div className="space-y-3">
            <Select
              label="Typing-Geschwindigkeit"
              description={getSpeedDescription(typingSpeed)}
              options={speedOptions}
              value={typingSpeed}
              onChange={onTypingSpeedChange}
            />
          </div>

          {/* Show Typing Indicator */}
          <div className="space-y-3">
            <Checkbox
              label="Typing-Indikator anzeigen"
              description="Zeigt animierte Punkte während der Bot tippt"
              checked={showTypingIndicator}
              onChange={(e) => onShowTypingIndicatorChange(e?.target?.checked)}
            />
          </div>
        </>
      )}
      {/* Typing Preview */}
      <div className="p-4 bg-muted rounded-lg">
        <p className="text-xs font-medium text-muted-foreground mb-4">Typing-Vorschau</p>
        
        <div className="bg-card border border-border rounded-lg p-4 max-w-sm">
          <div className="space-y-3">
            {/* User Message */}
            <div className="flex justify-end">
              <div className="bg-primary text-primary-foreground rounded-lg px-3 py-2 max-w-xs">
                <p className="text-sm">Wie sind Ihre Öffnungszeiten?</p>
              </div>
            </div>
            
            {/* Typing Indicator (if enabled) */}
            {enableTyping && showTypingIndicator && (
              <div className="flex items-start space-x-2">
                <div className="w-6 h-6 rounded-full bg-primary flex-shrink-0"></div>
                <div className="bg-muted rounded-lg px-3 py-2">
                  <div className="flex items-center space-x-1">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    <span className="text-xs text-muted-foreground ml-2">tippt...</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Bot Response */}
            <div className="flex items-start space-x-2">
              <div className="w-6 h-6 rounded-full bg-primary flex-shrink-0"></div>
              <div className="bg-background border border-border rounded-lg px-3 py-2 max-w-xs">
                <p className="text-sm text-foreground">
                  Wir sind Montag bis Freitag von 9:00 bis 18:00 Uhr für Sie da!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Speed Comparison */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {speedOptions?.map((option) => (
          <div 
            key={option?.value}
            className={`
              p-3 border rounded-lg text-center transition-all duration-200
              ${typingSpeed === option?.value && enableTyping
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }
            `}
          >
            <div className="text-sm font-medium text-foreground mb-1">
              {option?.label?.split(' ')?.[0]}
            </div>
            <div className="text-xs text-muted-foreground">
              {option?.label?.match(/\((.*?)\)/)?.[1] || ''}
            </div>
            <div className="mt-2 flex justify-center">
              {option?.value === 'instant' ? (
                <Icon name="Zap" size={16} className="text-warning" />
              ) : (
                <div className="flex space-x-1">
                  {Array.from({ length: option?.value === 'slow' ? 3 : option?.value === 'medium' ? 2 : 1 })?.map((_, i) => (
                    <div 
                      key={i}
                      className="w-1.5 h-1.5 bg-primary rounded-full"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Performance Note */}
      <div className="p-3 bg-accent/10 border border-accent/20 rounded-lg">
        <div className="flex items-start space-x-2">
          <div className="w-4 h-4 rounded-full bg-accent flex-shrink-0 mt-0.5"></div>
          <div>
            <p className="text-sm font-medium text-accent-foreground">Performance-Hinweis</p>
            <p className="text-xs text-accent-foreground/80 mt-1">
              Typing-Simulation kann die wahrgenommene Antwortzeit verlängern. 
              Für schnelle Support-Szenarien empfehlen wir "Schnell" oder "Sofort".
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TypingSimulationControls;